import React, { useState, useEffect } from 'react';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { apiFetch, setToken } from '../api';

// ── Design tokens ─────────────────────────────────────────────────────────────

const T = {
  bg:          '#f4ecd9',
  paper:       '#fdf6e3',
  paperHigh:   '#fffaf0',
  ink:         '#22180e',
  inkSoft:     '#6a5a3e',
  inkMute:     '#a89570',
  accent:      '#c75a2c',
  accentSoft:  '#f8dcc1',
  teal:        '#4e8a90',
  tealSoft:    '#cce0e2',
  divider:     'rgba(34,24,14,0.08)',
  display:     "'Newsreader', Georgia, serif",
  body:        "'Work Sans', system-ui, sans-serif",
  radius:      18,
  radiusLg:    28,
};

// ── Tiny icon ─────────────────────────────────────────────────────────────────

function ShieldIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke={T.teal} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l8 3v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function FaceIDGlyph({ pulsing }) {
  return (
    <div style={{
      width: 72, height: 72,
      background: T.accentSoft,
      borderRadius: 20,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: pulsing ? 'faceIDPulse 1.8s ease-in-out infinite' : 'none',
      boxShadow: '0 4px 16px -4px rgba(199,90,44,0.25)',
    }}>
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none"
        stroke={T.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4.5C4 3.7 4.7 3 5.5 3H11v17H5.5C4.7 20 4 19.3 4 18.5v-14z" />
        <path d="M20 4.5C20 3.7 19.3 3 18.5 3H13v17h5.5c.8 0 1.5-.7 1.5-1.5v-14z" />
        <path d="M11 3v17M13 3v17" />
        <path d="M7 7h1M7 10h1M7 13h1" />
        <path d="M16 7h1M16 10h1M16 13h1" />
      </svg>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('');
  const [mode, setMode] = useState('login'); // login | register
  const [phase, setPhase] = useState('idle'); // idle | waiting | device | done | error
  const [errorMsg, setErrorMsg] = useState('');

  // Load Google Fonts
  useEffect(() => {
    if (!document.getElementById('memories-fonts')) {
      const link = document.createElement('link');
      link.id = 'memories-fonts';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,300..700;1,6..72,300..700&family=Work+Sans:wght@300..700&display=swap';
      document.head.appendChild(link);
    }
    document.body.style.background = T.bg;
    document.body.style.color = T.ink;
    document.body.style.fontFamily = T.body;
    return () => {
      document.body.style.background = '';
      document.body.style.color = '';
      document.body.style.fontFamily = '';
    };
  }, []);

  const handleLogin = async () => {
    if (!email.trim()) return setErrorMsg('Please enter your email address.');
    setPhase('waiting'); setErrorMsg('');
    try {
      const options = await apiFetch('/auth/login/options', { method: 'POST', body: JSON.stringify({ email: email.trim() }) });
      const { challengeId, userId, ...authOptions } = options;
      setPhase('device');
      const credential = await startAuthentication(authOptions);
      const result = await apiFetch('/auth/login/verify', { method: 'POST', body: JSON.stringify({ ...credential, challengeId, userId }) });
      if (result.verified) { setPhase('done'); setToken(result.token); onLogin(result.user); }
    } catch (e) {
      if (e.message?.includes('No passkey registered')) {
        setMode('register'); setPhase('idle');
        setErrorMsg("We couldn't find a passkey for that email. Let's set one up.");
      } else {
        setPhase('error'); setErrorMsg(e.message || 'Something went wrong. Please try again.');
      }
    }
  };

  const handleRegister = async () => {
    if (!email.trim()) return setErrorMsg('Please enter your email address.');
    setPhase('waiting'); setErrorMsg('');
    try {
      const options = await apiFetch('/auth/register/options', { method: 'POST', body: JSON.stringify({ email: email.trim() }) });
      const { challengeId, userId, ...regOptions } = options;
      setPhase('device');
      const credential = await startRegistration(regOptions);
      const result = await apiFetch('/auth/register/verify', { method: 'POST', body: JSON.stringify({ ...credential, challengeId, userId }) });
      if (result.verified) { setPhase('done'); setToken(result.token); onLogin(result.user); }
    } catch (e) {
      setPhase('error'); setErrorMsg(e.message || 'Something went wrong. Please try again.');
    }
  };

  const busy = phase === 'waiting' || phase === 'device' || phase === 'done';
  const pulsing = phase === 'device';

  return (
    <>
      <style>{`
        @keyframes faceIDPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%       { transform: scale(1.04); opacity: 0.85; }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .memories-login-input:focus {
          outline: none;
          border-color: ${T.accent} !important;
          box-shadow: 0 0 0 3px ${T.accentSoft};
        }
        .memories-login-btn:active { opacity: 0.88; transform: scale(0.98); }
      `}</style>

      <div style={{
        minHeight: '100vh',
        background: T.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
        fontFamily: T.body,
      }}>
        <div style={{
          width: '100%', maxWidth: 360,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          animation: 'fadeUp 0.35s ease',
        }}>

          {/* Wordmark */}
          <div style={{ marginBottom: 40, textAlign: 'center' }}>
            <div style={{
              fontFamily: T.display,
              fontSize: 34, fontWeight: 400, letterSpacing: -0.5,
              color: T.ink, lineHeight: 1,
            }}>
              memories<span style={{ color: T.accent }}>.</span>
            </div>
            <div style={{
              fontFamily: T.body, fontSize: 13, fontWeight: 700,
              letterSpacing: '0.2em', textTransform: 'uppercase',
              color: T.inkMute, marginTop: 6,
            }}>
              {mode === 'login' ? 'Welcome back' : 'First time here'}
            </div>
          </div>

          {/* Face ID glyph */}
          <div style={{ marginBottom: 32 }}>
            <FaceIDGlyph pulsing={pulsing} />
          </div>

          {/* Headline */}
          <h1 style={{
            fontFamily: T.display, fontWeight: 400,
            fontSize: 28, letterSpacing: -0.4, lineHeight: 1.15,
            color: T.ink, textAlign: 'center', margin: '0 0 8px',
          }}>
            {phase === 'device'
              ? <>Your phone will{' '}<em style={{ color: T.accent }}>recognize you.</em></>
              : phase === 'done'
              ? <>You're{' '}<em style={{ color: T.accent }}>in.</em></>
              : mode === 'login'
              ? <>Sign{' '}<em style={{ color: T.accent }}>in.</em></>
              : <>Set up your{' '}<em style={{ color: T.accent }}>account.</em></>
            }
          </h1>

          <p style={{
            fontFamily: T.body, fontSize: 16, color: T.inkSoft,
            textAlign: 'center', margin: '0 0 32px', lineHeight: 1.55,
          }}>
            {phase === 'device'
              ? 'Follow the prompt — the same way you unlock your phone.'
              : phase === 'done'
              ? 'Taking you to your family album…'
              : mode === 'login'
              ? 'Enter your email and your phone will do the rest.'
              : 'Enter your email to get started. No password needed.'
            }
          </p>

          {/* Form — hidden during device/done phases */}
          {phase !== 'device' && phase !== 'done' && (
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input
                className="memories-login-input"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (mode === 'login' ? handleLogin() : handleRegister())}
                autoComplete="email"
                autoCapitalize="none"
                disabled={busy}
                style={{
                  width: '100%',
                  background: T.paper,
                  border: `1.5px solid ${T.divider}`,
                  borderRadius: T.radius,
                  padding: '16px 18px',
                  fontSize: 17,
                  fontFamily: T.body,
                  color: T.ink,
                  boxSizing: 'border-box',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
              />

              {/* Error */}
              {errorMsg && (
                <div style={{
                  background: '#fdf3e8',
                  border: `1px solid ${T.accentSoft}`,
                  borderRadius: 12, padding: '12px 14px',
                  fontFamily: T.body, fontSize: 14, color: T.inkSoft,
                  lineHeight: 1.5,
                }}>
                  {errorMsg}
                </div>
              )}

              {/* Primary button */}
              <button
                className="memories-login-btn"
                onClick={mode === 'login' ? handleLogin : handleRegister}
                disabled={busy}
                style={{
                  width: '100%', minHeight: 56,
                  background: T.ink,
                  border: 'none', borderRadius: T.radiusLg,
                  fontFamily: T.body, fontSize: 17, fontWeight: 600,
                  color: T.paper, cursor: busy ? 'default' : 'pointer',
                  opacity: busy ? 0.6 : 1,
                  transition: 'opacity 0.15s, transform 0.1s',
                  letterSpacing: 0.1,
                }}
              >
                {phase === 'waiting'
                  ? 'One moment…'
                  : mode === 'login'
                  ? 'Sign in'
                  : 'Set up my account'
                }
              </button>

              {/* Mode toggle */}
              <button
                onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setErrorMsg(''); setPhase('idle'); }}
                style={{
                  background: 'none', border: 'none',
                  fontFamily: T.body, fontSize: 14, color: T.inkMute,
                  cursor: 'pointer', padding: '6px 0',
                  textDecoration: 'underline', textDecorationColor: T.divider,
                }}
              >
                {mode === 'login' ? 'First time? Set up your account' : 'Already set up? Sign in instead'}
              </button>
            </div>
          )}

          {/* Teal privacy pill */}
          {phase === 'idle' || phase === 'error' ? (
            <div style={{
              marginTop: 32,
              display: 'flex', alignItems: 'center', gap: 8,
              background: T.tealSoft,
              border: `1px solid ${T.teal}22`,
              borderRadius: 999, padding: '9px 16px',
            }}>
              <ShieldIcon />
              <span style={{
                fontFamily: T.body, fontSize: 13, color: T.teal, fontWeight: 500,
              }}>
                Your phone recognizes you — no password ever stored.
              </span>
            </div>
          ) : null}

        </div>
      </div>
    </>
  );
}
